<template>
    <v-dialog
    v-model="localDialog"
    width="35%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
    >
        <v-card>
            <v-card-title class="text-h6">
                <span>{{ isEntryMode ? "Add Dyeing Setup" : "Update Dyeing Setup" }}</span>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text>
                <v-form @submit.prevent="submit" ref="DyeingSetup">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                            label="Dyeing House Name"
                            density="compact"
                            variant="outlined"
                            bg-color="grey-lighten-4"
                            v-model="name.value.value"
                            :error-messages="name.errorMessage.value"
                            clearable
                            >
                                <template #label>
                                    Dyeing House Name <span class="text-red-accent-4">*</span>
                                </template>
                            </v-text-field>
                            <v-text-field
                                label="Contact Person (Optional)"
                                density="compact"
                                variant="outlined"
                                bg-color="grey-lighten-4"
                                v-model="contact_person.value.value"
                                :error-messages="contact_person.errorMessage.value"
                                clearable
                            ></v-text-field>
                            <v-text-field
                                label="Contact Phone (Optional)"
                                density="compact"
                                variant="outlined"
                                bg-color="grey-lighten-4"
                                v-model="contact_phone.value.value"
                                :error-messages="contact_phone.errorMessage.value"
                                clearable
                            ></v-text-field>
                            <v-text-field
                                label="Contact Email (Optional)"
                                density="compact"
                                variant="outlined"
                                bg-color="grey-lighten-4"
                                v-model="contact_email.value.value"
                                :error-messages="contact_email.errorMessage.value"
                                clearable
                            ></v-text-field>
                            <v-textarea
                            label="Address (Optional)"
                            density="compact"
                            variant="outlined"
                            bg-color="grey-lighten-4"
                            v-model="address.value.value"
                            :error-messages="address.errorMessage.value"
                            clearable
                            auto-grow
                            rows="2"
                            ></v-textarea>
                        </v-col>

                        <v-col cols="12">
                            <v-radio-group
                                v-model="type.value.value"
                                :error-messages="type.errorMessage.value"
                                label="Type"
                                inline
                            >
                                <v-radio label="In House" value="in_house"></v-radio>
                                <v-radio label="External" value="external"></v-radio>
                            </v-radio-group>
                        </v-col>

                        <v-checkbox
                            label="Active"
                            v-model="status.value.value"
                            :error-messages="status.errorMessage.value"
                            density="compact"
                            variant="outlined"
                            hide-details
                            single-line
                            color="primary"
                            true-value="1"
                            false-value="0"
                        ></v-checkbox>
                    </v-row>

                    <v-divider></v-divider>

                    <v-card-actions>
                        <v-layout class="justify-end">
                            <v-btn
                            class="bg-red"
                            variant="flat"
                            type="reset"
                            @click="closeDialog"
                            >
                                Close
                            </v-btn>

                            <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                                {{ isEntryMode ? "Save" : "Update" }}
                            </v-btn>
                        </v-layout>
                    </v-card-actions>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import { fileServerUrl } from "@/store/api";

export default {
    name: "DyeingSetup",
    props: {
        dialog: {
            type: Boolean,
            default: false,
        },

        data: {
            type: Object,
            default: () => ({
                id: null,
                name: "",
                contact_person: "",
                contact_phone: "",
                contact_email: "",
                address: "",
                type: "external",
                status: "1",
            }),
        },
    },

    data() {
        return {
            localDialog: this.dialog,
            errorMessages: "",
            loading: false,
            isEntryMode: true,
        };
    },

    computed: {},

    watch: {
        dialog(val) {
            this.localDialog = val;
        },
        localDialog(val) {
            this.$emit("update:dialog", val);
        },
        data: {
            handler(newData) {
                this.isEntryMode = !newData.id;
                this.resetForm({ errors: {}, touched: {} });
                this.id.setValue(newData.id || null, false);
                this.name.setValue(newData.name || "", false);
                this.contact_person.setValue(newData.contact_person || "", false);
                this.contact_phone.setValue(newData.contact_phone || "", false);
                this.contact_email.setValue(newData.contact_email || "", false);
                this.address.setValue(newData.address || "", false);
                this.type.setValue(newData.type || "external", false);
                this.status.setValue(String(newData.status ?? "1"), false);
            },
        },
    },

    created() {},
    
    methods: {
        updateDialog(val) {
            this.$emit("update:dialog", val);
        },
        closeDialog() {
            this.$emit("update:dialog", false);
        },
    },

    setup(props, { emit }) {
        const { handleSubmit, resetForm } = useForm({
            validationSchema: yup.object({
                name: yup.string().required().label("Dyeing House Name"),
                contact_person: yup.string().nullable().label("Contact Person"),
                contact_phone: yup.string().nullable().label("Contact Phone"),
                contact_email: yup
                    .string()
                    .nullable()
                    .email()
                    .label("Contact Email"),
                address: yup.string().nullable().label("Address"),
                type: yup
                    .string()
                    .oneOf(["in_house", "external"])
                    .required()
                    .label("Type"),
                status: yup
                    .string()
                    .oneOf(["0", "1"])
                    .required()
                    .label("Status"),
            }),
        });

        const id = useField("id");
        const name = useField("name");
        const contact_person = useField("contact_person");
        const contact_phone = useField("contact_phone");
        const contact_email = useField("contact_email");
        const type = useField("type");
        const status = useField("status");
        const address = useField("address");
        const errorMessages = ref("");
        const loading = ref("");
        const store = useStore();

        const submit = handleSubmit((values) => {
            let action;
            const recordId = values.id;
            let payload = { ...values };
            
            if(recordId){
                action = "updateDyeing";
                // Store id separately for URL, remove from payload
                payload.id = recordId;
            } else {
                action = "saveDyeing";
                // Remove id from payload for create
                delete payload.id;
            }

            store
            .dispatch(action, payload)
            .then((response) => {
                resetForm();
                emit("update:dialog", false);
                toast.success(response.data.message);
                loading.value = true;
                store
                .dispatch("getDyeingList")
                .then((data) => {
                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                    toast.error(error.response.data.message);
                });
            })
            .catch((error) => {
                toast.error(error.response.data.message);
            });
        });

        return {
            submit,
            id,
            name,
            contact_person,
            contact_phone,
            contact_email,
            type,
            status,
            address,
            errorMessages,
            resetForm,
            loading,
        };
    },
    mounted() {},
};
</script>
